/**
 * The site map — a commercial lot drawn over satellite imagery.
 *
 * Owner, Oct 2 2026: "add a feature for commercial lots with a map where I
 * can mark what gets done on the property and I can add locks and codes to
 * the locks for the crew."
 *
 * Two faces of the same map, one vocabulary (services/siteMarks on the server):
 *   SiteMapEditor — the office draws: tap the corners of an area, drop a lock,
 *                   gate or note pin, drag a pin to move it, tap a mark to
 *                   edit it. Nothing is saved until Save (one replace-all PUT).
 *   SiteMapView   — the crew's read-only map. LocksCard and WorkHereList are
 *                   the same marks in plain words, readable with no map at all.
 *
 * No drawing library: react-leaflet's own taps and draggable markers are
 * enough for a lot with a handful of areas, and it works with a thumb at
 * 375px — big buttons along the bottom, a sheet for the editor. Esri World
 * Imagery for the satellite view (true colour even in dark mode — index.css
 * leaves `.kam-satellite` out of the dark-map filter), OpenStreetMap for the
 * street view.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { MapContainer, TileLayer, Marker, Polygon, Polyline, CircleMarker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowLeft, Check, DoorOpen, Hexagon, Lock, MapPin, Maximize2, StickyNote, Trash2, Undo2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

// ── Shapes (mirrors services/siteMarks) ───────────────────────────────────────
export type MarkKind = 'area' | 'lock' | 'gate' | 'note';
type PinKind = Exclude<MarkKind, 'area'>;
export type LatLng = [number, number];
export type Geometry = { type: 'polygon'; points: LatLng[] } | { type: 'point'; point: LatLng };
export interface SiteMark {
  id?: number | string;          // a number once saved; a 'tmp-…' string while drawing
  kind: MarkKind;
  label: string | null;
  work: string | null;           // an area: what gets done there
  code: string | null;           // a lock or gate code — crew only
  notes: string | null;
  color: string | null;
  geometry: Geometry;
  sort_order?: number;
}
export interface SiteProperty {
  id: number; customer_id: number; label: string | null; address: string; city: string | null;
  lat: number | null; lng: number | null; guess?: { lat: number; lng: number } | null;
}
export interface SiteMarksPayload { property: SiteProperty; marks: SiteMark[]; home: { lat: number; lng: number }; work_picks: string[] }

const SAT_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
// How far in the map goes, and how far in each provider actually has pictures.
// Owner, Nov 2026: "let it zoom in further on a yard" — a bed, a gate or a lock is
// a few metres across and z19 was as close as the map would come.
//
// maxNativeZoom is the one that matters past the sharpest real tile: Leaflet
// stretches the tile it has (softer, still the right place) instead of asking for
// one that does not exist and drawing grey. Measured against ArcGIS on Nov 2 2026,
// over Hutto, Pflugerville and central Austin: real imagery to z21 (a tile that
// gets smaller and still different at each level), and z22 / z23 answer 200 with
// one identical 2,521-byte "no data" tile everywhere — exactly the blank this
// avoids. OpenStreetMap's own tiles stop at z19.
const MAX_ZOOM = 22;            // one step past the sharpest satellite tile, stretched
const SAT_NATIVE_ZOOM = 21;     // Esri World Imagery over Austin
const OSM_NATIVE_ZOOM = 19;     // openstreetmap.org tiles
const FIT_MAX_ZOOM = 19;        // how close FitTo opens on its own
export const WORK_PICKS = ['Mow', 'Trim / edge', 'Spray', 'Cleanup', 'Leave alone'];
// An area's fill follows its work unless the office picked a colour.
export const WORK_COLORS: Record<string, string> = { 'Mow': '#22c55e', 'Trim / edge': '#f59e0b', 'Spray': '#14b8a6', 'Cleanup': '#a855f7', 'Leave alone': '#ef4444' };
const OTHER_COLOR = '#64748b';
const SWATCHES = ['#22c55e', '#f59e0b', '#14b8a6', '#a855f7', '#ef4444', '#3b82f6', '#ec4899', '#64748b'];
export function markColor(m: Pick<SiteMark, 'color' | 'work'>): string { return m.color || WORK_COLORS[m.work || ''] || OTHER_COLOR; }
const KIND_WORD: Record<MarkKind, string> = { area: 'Area', lock: 'Lock', gate: 'Gate', note: 'Note' };
const PIN_BG: Record<PinKind, string> = { lock: '#d97706', gate: '#2563eb', note: '#475569' };
const PIN_GLYPH: Record<PinKind, string> = { lock: '🔒', gate: '🚪', note: '✎' };
const BRAND = '#df00ff';

const num = (v: any) => (v == null || v === '' ? NaN : Number(v));
const pair = (p: any): LatLng | null => (Array.isArray(p) && Number.isFinite(num(p[0])) && Number.isFinite(num(p[1])) ? [num(p[0]), num(p[1])] : null);
/** A mark as the server sent it (JSONB may arrive as text) → one we can draw. */
export function normalizeMark(m: any): SiteMark | null {
  let g = m?.geometry;
  if (typeof g === 'string') { try { g = JSON.parse(g); } catch { return null; } }
  let geometry: Geometry | null = null;
  if (g?.type === 'polygon' && Array.isArray(g.points)) {
    const points = g.points.map(pair).filter(Boolean) as LatLng[];
    if (points.length >= 3) geometry = { type: 'polygon', points };
  } else if (g?.type === 'point') {
    const point = pair(g.point);
    if (point) geometry = { type: 'point', point };
  }
  if (!geometry) return null;
  return { id: m.id, kind: m.kind, label: m.label ?? null, work: m.work ?? null, code: m.code ?? null, notes: m.notes ?? null, color: m.color ?? null, geometry, sort_order: m.sort_order };
}
export const normalizeMarks = (list: any[] | undefined | null): SiteMark[] => (list || []).map(normalizeMark).filter(Boolean) as SiteMark[];
function pointsOf(marks: SiteMark[]): LatLng[] {
  return marks.flatMap(m => (m.geometry.type === 'polygon' ? m.geometry.points : [m.geometry.point]));
}
/** "Front lawn" over "Mow" — two lines on purpose. Joined with ' · ' the dot broke onto a line of its own at 150px and the label read as four stacked words. */
const AreaLabel = ({ m }: { m: SiteMark }) => (
  <>
    {(m.label || !m.work) && <span className="block">{m.label || 'Area'}</span>}
    {m.work && <span className="block font-semibold">{m.work}</span>}
  </>
);
/** "2 areas · 1 lock/gate · 1 note" — the same words the Approves page uses for a manager's save. */
export function markCounts(marks: SiteMark[]): string {
  const n = (...kinds: MarkKind[]) => marks.filter(m => kinds.includes(m.kind)).length;
  const areas = n('area'), locks = n('lock', 'gate'), notes = n('note');
  return [
    areas ? `${areas} area${areas === 1 ? '' : 's'}` : null,
    locks ? `${locks} lock${locks === 1 ? '' : 's'}/gate${locks === 1 ? '' : 's'}` : null,
    notes ? `${notes} note${notes === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
}
const pinWords = (m: SiteMark) => m.label || KIND_WORD[m.kind];

// ── Map pieces shared by the editor and the crew view ─────────────────────────
const SITE_CSS = `
.kam-site-pin { display:flex; align-items:center; justify-content:center; width:30px; height:30px; border-radius:9999px; border:2px solid #fff; box-shadow:0 1px 4px rgba(0,0,0,.5); font-size:15px; line-height:1; color:#fff; cursor:pointer; }
.kam-site-pin-selected { outline:3px solid ${BRAND}; outline-offset:1px; }
.kam-draft-dot { width:14px; height:14px; border-radius:9999px; background:#fff; border:3px solid ${BRAND}; box-shadow:0 0 4px rgba(0,0,0,.5); }
.kam-site-map .leaflet-tooltip.kam-area-label { background:transparent; border:0; box-shadow:none; color:#fff; font-weight:800; font-size:12px; line-height:1.2; text-shadow:0 1px 3px rgba(0,0,0,.95), 0 0 2px rgba(0,0,0,.95); padding:0; white-space:normal; width:max-content; max-width:150px; text-align:center; }
/* width:max-content — Leaflet places a tooltip inside a zero-width pane, so without it the box shrinks to its longest word and "Retention pond" stacks. */
.kam-site-map .leaflet-tooltip.kam-area-label:before { display:none; }
.kam-site-map .leaflet-container { font-family: inherit; }
`;
function pinIcon(kind: PinKind, selected: boolean) {
  return L.divIcon({
    className: '',
    html: `<div class="kam-site-pin${selected ? ' kam-site-pin-selected' : ''}" style="background:${PIN_BG[kind]}">${PIN_GLYPH[kind]}</div>`,
    iconSize: [30, 30], iconAnchor: [15, 15], tooltipAnchor: [0, -16],
  });
}
const draftDot = L.divIcon({ className: '', html: '<div class="kam-draft-dot"></div>', iconSize: [14, 14], iconAnchor: [7, 7] });

type Tiles = 'sat' | 'street';
const TILES_KEY = 'kam.siteMap.tiles';
function useTiles(): [Tiles, (t: Tiles) => void] {
  // Remembered on this phone only — a convenience, nothing more.
  const [tiles, setTiles] = useState<Tiles>(() => { try { return localStorage.getItem(TILES_KEY) === 'street' ? 'street' : 'sat'; } catch { return 'sat'; } });
  const set = (t: Tiles) => { setTiles(t); try { localStorage.setItem(TILES_KEY, t); } catch { /* private window */ } };
  return [tiles, set];
}
function TileChips({ tiles, onChange }: { tiles: Tiles; onChange: (t: Tiles) => void }) {
  const chip = (on: boolean) => `press text-xs font-bold px-3 min-h-[36px] rounded-full border shadow-sm ${on ? 'bg-plum text-snow border-plum' : 'bg-white text-gray-800 border-gray-300'}`;
  return (
    <div className="absolute top-2 right-2 z-10 flex gap-1">
      <button type="button" onClick={() => onChange('street')} className={chip(tiles === 'street')}>Street</button>
      <button type="button" onClick={() => onChange('sat')} className={chip(tiles === 'sat')}>Satellite</button>
    </div>
  );
}
function TileSet({ kind }: { kind: Tiles }) {
  return kind === 'sat'
    ? <TileLayer key="sat" className="kam-satellite" maxZoom={MAX_ZOOM} maxNativeZoom={SAT_NATIVE_ZOOM} attribution="Tiles © Esri" url={SAT_URL} />
    : <TileLayer key="street" maxZoom={MAX_ZOOM} maxNativeZoom={OSM_NATIVE_ZOOM} attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url={OSM_URL} />;
}
/** Frame every mark (and the property pin). `once`: only on the first paint, so a map being drawn on does not jump. */
function FitTo({ points, fallback, zoom, once }: { points: LatLng[]; fallback: LatLng; zoom: number; once?: boolean }) {
  const map = useMap();
  const done = useRef(false);
  const key = points.map(p => p.join(',')).join('|') + '#' + fallback.join(',');
  useEffect(() => {
    if (once && done.current) return;
    done.current = true;
    // Framing is unchanged — a map of two marks a metre apart must not open on top of them.
    // Going closer than this is his pinch, not ours.
    if (points.length >= 2) map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: FIT_MAX_ZOOM });
    else if (points.length === 1) map.setView(points[0], zoom);
    else map.setView(fallback, zoom);
    // key is the point list; points itself is a new array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}
function Taps({ onTap }: { onTap: (p: LatLng) => void }) {
  useMapEvents({ click: e => onTap([e.latlng.lat, e.latlng.lng]) });
  return null;
}
/** The marks themselves. Pins drag when `editable`; taps report back with where they landed. */
function MarkLayers({ marks, editable = false, selected = null, onTap, onMove }: {
  marks: SiteMark[]; editable?: boolean; selected?: number | null;
  onTap?: (i: number, at: LatLng) => void; onMove?: (i: number, to: LatLng) => void;
}) {
  return (
    <>
      {marks.map((m, i) => {
        const key = m.id ?? `i${i}`;
        if (m.geometry.type === 'polygon') {
          const c = markColor(m);
          return (
            <Polygon key={key} positions={m.geometry.points}
              // bubblingMouseEvents off: a tap on an area is reported once, by the area, not again by the map underneath
              pathOptions={{ color: c, fillColor: c, fillOpacity: selected === i ? 0.5 : 0.35, weight: selected === i ? 4 : 2, bubblingMouseEvents: false }}
              eventHandlers={{ click: e => onTap?.(i, [e.latlng.lat, e.latlng.lng]) }}>
              <Tooltip permanent direction="center" className="kam-area-label"><AreaLabel m={m} /></Tooltip>
            </Polygon>
          );
        }
        return (
          <Marker key={key} position={m.geometry.point} icon={pinIcon(m.kind as PinKind, selected === i)} draggable={editable}
            eventHandlers={{
              click: e => onTap?.(i, [e.latlng.lat, e.latlng.lng]),
              dragend: e => { const ll = e.target.getLatLng(); onMove?.(i, [ll.lat, ll.lng]); },
            }}>
            <Tooltip direction="top">{pinWords(m)}</Tooltip>
          </Marker>
        );
      })}
    </>
  );
}
const PropertyPin = ({ at }: { at: LatLng }) => (
  <CircleMarker center={at} radius={6} pathOptions={{ color: BRAND, fillColor: '#fff', fillOpacity: 1, weight: 3, interactive: false }} />
);

// ── The crew's read-only pieces ───────────────────────────────────────────────

/**
 * The map as the crew see it: areas with their work, the pins, no taps.
 * `pannable` off: a thumb swipe over the map scrolls the page instead of
 * dragging the map — right for a map sitting in a card on the job page
 * (pinch zoom still works). SiteMapCrewCard opens it full screen to move around.
 */
export function SiteMapView({ marks, pin, className, pannable = true }: { marks: SiteMark[]; pin?: LatLng | null; className?: string; pannable?: boolean }) {
  const [tiles, setTiles] = useTiles();
  const points = useMemo(() => pointsOf(marks).concat(pin ? [pin] : []), [marks, pin]);
  if (!points.length) return null;
  return (
    <div className={`relative kam-site-map ${className || 'h-72'}`}>
      <style>{SITE_CSS}</style>
      <MapContainer center={points[0]} zoom={17} maxZoom={MAX_ZOOM} scrollWheelZoom={false} dragging={pannable} style={{ height: '100%', width: '100%' }}>
        <TileSet kind={tiles} />
        <FitTo points={points} fallback={points[0]} zoom={18} />
        <MarkLayers marks={marks} />
        {pin && <PropertyPin at={pin} />}
      </MapContainer>
      <TileChips tiles={tiles} onChange={setTiles} />
    </div>
  );
}

/** The map in a card on the crew's job page: framed, not draggable, with "Open full map" for when they need to move around it. */
export function SiteMapCrewCard({ marks, pin }: { marks: SiteMark[]; pin?: LatLng | null }) {
  const [full, setFull] = useState(false);
  // Escape closes the full map, same as the editor's sheet.
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFull(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);
  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <SiteMapView marks={marks} pin={pin} className="h-72" pannable={false} />
        <button type="button" onClick={() => setFull(true)}
          className="press w-full min-h-[48px] border-t border-gray-200 text-sm font-bold text-gray-900 flex items-center justify-center gap-1.5">
          <Maximize2 className="h-4 w-4" /> Open full map
        </button>
      </div>
      {full && (
        // Same header as the editor: bg-white / text-gray-900 are the tokens index.css flips for dark mode, so this reads right in both.
        <div className="fixed inset-0 z-50 flex flex-col bg-white text-gray-900" role="dialog" aria-modal="true" aria-label="Site map">
          <div className="flex items-center gap-2 px-2 py-2 flex-shrink-0 border-b border-gray-200">
            <button type="button" onClick={() => setFull(false)} aria-label="Back" className="press h-11 w-11 flex items-center justify-center rounded-xl hover:bg-gray-100">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <p className="text-sm font-black uppercase tracking-tight">Site map</p>
          </div>
          <SiteMapView marks={marks} pin={pin} className="flex-1 min-h-0" />
        </div>
      )}
    </>
  );
}

/** Every lock and gate in plain text — the code big enough to read through a windshield. */
export function LocksCard({ marks }: { marks: SiteMark[] }) {
  const locks = marks.filter(m => m.kind === 'lock' || m.kind === 'gate');
  if (!locks.length) return null;
  return (
    <div className="bg-amber-400 rounded-xl p-4 shadow-md">
      <p className="text-xs font-black text-amber-950 uppercase tracking-wide mb-2">🔒 Locks &amp; codes</p>
      <ul className="divide-y divide-amber-950/15">
        {locks.map((l, i) => (
          <li key={l.id ?? i} className="py-2 first:pt-0 last:pb-0">
            <p className="text-sm font-bold text-amber-950">{l.label || KIND_WORD[l.kind]}{l.label ? <span className="font-medium"> · {KIND_WORD[l.kind].toLowerCase()}</span> : null}</p>
            {l.code
              ? <p className="text-3xl font-black tabular-nums tracking-wider text-amber-950 break-all leading-tight">{l.code}</p>
              : <p className="text-sm text-amber-950/80">No code — it is unlocked, or the office has the key</p>}
            {l.notes && <p className="text-sm text-amber-950 mt-0.5">{l.notes}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The areas and their work, as a list — and any notes pinned on the map. */
export function WorkHereList({ marks }: { marks: SiteMark[] }) {
  const areas = marks.filter(m => m.kind === 'area');
  const notes = marks.filter(m => m.kind === 'note');
  if (!areas.length && !notes.length) return null;
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">What gets done here</p>
      {areas.length > 0 && (
        <ul className="space-y-2">
          {areas.map((a, i) => (
            <li key={a.id ?? i} className="flex items-start gap-2.5">
              <span className="mt-1.5 h-3 w-3 rounded-full flex-shrink-0 border border-black/20" style={{ background: markColor(a) }} />
              <div className="min-w-0">
                <p className="text-base font-extrabold text-gray-900 leading-snug">
                  {a.label || 'Area'}{a.work ? <span className="font-bold text-gray-700"> — {a.work}</span> : null}
                </p>
                {a.notes && <p className="text-sm text-gray-600">{a.notes}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {notes.length > 0 && (
        <ul className={`space-y-1.5 ${areas.length ? 'mt-3 pt-3 border-t border-gray-200' : ''}`}>
          {notes.map((n, i) => (
            <li key={n.id ?? i} className="flex items-start gap-2.5">
              <span className="text-gray-500 flex-shrink-0">✎</span>
              <p className="text-sm text-gray-800"><span className="font-bold">{n.label || 'Note'}</span>{n.notes ? ` — ${n.notes}` : ''}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── The office's editor ───────────────────────────────────────────────────────
type Mode = 'idle' | 'area' | 'pin' | PinKind;   // 'pin' = move the property's own pin
const tmpId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default function SiteMapEditor({ data, onClose }: { data: SiteMarksPayload; onClose: () => void }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { property, home } = data;
  const picks = data.work_picks?.length ? data.work_picks : WORK_PICKS;
  const [marks, setMarks] = useState<SiteMark[]>(() => normalizeMarks(data.marks));
  const [mode, setMode] = useState<Mode>('idle');
  const [draft, setDraft] = useState<LatLng[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [tiles, setTiles] = useTiles();

  // The property's own pin. The geocoders guess along the road and can land a big lot hundreds of feet off
  // (owner, Oct 2 2026: "its marking wrong place on map") — "Move the pin" lets the office put it where the
  // property really is; the yard's pin is what routes, the Client map and this map all read.
  const [pin, setPin] = useState<LatLng | null>(property.lat != null && property.lng != null ? [property.lat, property.lng] : null);
  const centre: LatLng = pin || (property.guess ? [property.guess.lat, property.guess.lng] : [home.lat, home.lng]);
  const movePin = useMutation<any, any, LatLng>(async (to) => { const r = await api.post(`/properties/${property.id}/coords`, { lat: to[0], lng: to[1] }); return r.data; }, {
    onSuccess: (_d: any, to: LatLng) => {
      setPin(to); setMode('idle');
      toast.success('Pin moved — routes and the Client map use the new spot');
      qc.invalidateQueries(['site-marks', property.id]); qc.invalidateQueries(['commercial']); qc.invalidateQueries('customers');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not move the pin'); },
  });
  // Framed once, on open: the marks and the pin. After that the map stays where the office put it.
  const [initialPoints] = useState<LatLng[]>(() => pointsOf(normalizeMarks(data.marks)).concat(pin ? [pin] : []));

  const update = (f: (ms: SiteMark[]) => SiteMark[]) => { setMarks(f); setDirty(true); };
  const addMark = (m: SiteMark) => { update(ms => [...ms, m]); setEditing(marks.length); setMode('idle'); };

  const onMapTap = (p: LatLng) => {
    if (mode === 'area') setDraft(d => [...d, p]);
    else if (mode === 'pin') { if (!movePin.isLoading) movePin.mutate(p); }
    else if (mode !== 'idle') addMark({ id: tmpId(), kind: mode, label: null, work: null, code: null, notes: null, color: null, geometry: { type: 'point', point: p } });
  };
  // A tap on an existing mark: edit it — unless something is being drawn, in which case it is just a tap on the map.
  const onMarkTap = (i: number, at: LatLng) => { if (mode === 'idle') setEditing(i); else onMapTap(at); };
  const finishArea = () => {
    if (draft.length < 3) return;
    addMark({ id: tmpId(), kind: 'area', label: null, work: picks[0] || null, code: null, notes: null, color: null, geometry: { type: 'polygon', points: draft } });
    setDraft([]);
  };
  const move = (i: number, to: LatLng) => update(ms => ms.map((m, j) => (j === i && m.geometry.type === 'point' ? { ...m, geometry: { type: 'point', point: to } } : m)));

  const save = useMutation(() => api.put(`/properties/${property.id}/site-marks`, { marks }).then(r => r.data), {
    onSuccess: (d: any) => {
      setMarks(normalizeMarks(d?.marks));
      setDirty(false);
      toast.success(d?.message || 'Site map saved');
      qc.invalidateQueries(['site-marks', property.id]);
      qc.invalidateQueries(['commercial']);
    },
    onError: (e: any) => {
      if (e?.isReadOnlyBlock) return;
      const msg: string = e?.response?.data?.message || 'Could not save the site map';
      toast.error(msg);
      // "Mark 3 ("Far lock") is more than a kilometre…" — open that mark, so
      // Delete is one tap away (nothing on the map is numbered).
      const n = Number((/^Mark (\d+)/.exec(msg) || [])[1]);
      if (n >= 1 && n <= marks.length) { setMode('idle'); setEditing(n - 1); }
    },
  });

  const close = () => {
    if (dirty && !window.confirm('Leave without saving? What you drew will be lost.')) return;
    onClose();
  };
  // Leaving the page by other means (back button, closing the tab) asks too.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const hint = mode === 'area'
    ? (draft.length === 0 ? 'Tap the first corner of the area' : draft.length < 3 ? `${draft.length} corner${draft.length === 1 ? '' : 's'} — keep tapping` : `${draft.length} corners — tap more, or Done`)
    : mode === 'pin' ? 'Tap where the property really is — the pin moves there'
    : mode !== 'idle' ? `Tap the map where the ${mode} is` : '';
  const counts = markCounts(marks);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white text-gray-900">
      <style>{SITE_CSS}</style>
      <div className="flex items-center gap-2 px-2 sm:px-3 py-2 border-b border-gray-200 bg-white">
        <button type="button" onClick={close} aria-label="Back" className="press h-11 w-11 flex items-center justify-center rounded-xl hover:bg-gray-100 flex-shrink-0">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black uppercase tracking-tight truncate">Site map</p>
          <p className="text-xs text-gray-600 truncate">{[property.label, property.address, property.city].filter(Boolean).join(' · ')}{counts ? ` — ${counts}` : ''}</p>
        </div>
        <button type="button" onClick={() => save.mutate()} disabled={readOnly || !dirty || save.isLoading || mode !== 'idle'}
          className="btn-primary min-h-[44px] px-4 flex-shrink-0">
          {save.isLoading ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </button>
      </div>
      {!pin && (
        <div className="bg-amber-100 text-amber-900 text-xs px-3 py-2 leading-snug">
          <span className="font-bold">Pin the property first</span> — <Link to={`/customers?profile=${property.customer_id}`} className="underline font-semibold">Customers → yard → address</Link>.
          {property.guess ? ' Until then the map opens on the address as best we can tell.' : ' Until then the map opens on home base.'}
          {' '}Anything drawn more than a kilometre from where the pin lands will have to be redrawn.
        </div>
      )}
      {readOnly && <div className="bg-gray-100 text-gray-700 text-xs px-3 py-2">View-only account — the map can be looked at, not changed.</div>}

      <div className="relative flex-1 min-h-0 kam-site-map">
        <MapContainer center={centre} zoom={18} maxZoom={MAX_ZOOM} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
          <TileSet kind={tiles} />
          <FitTo points={initialPoints} fallback={centre} zoom={18} once />
          <Taps onTap={onMapTap} />
          <MarkLayers marks={marks} editable={mode === 'idle' && !readOnly} selected={editing} onTap={onMarkTap} onMove={move} />
          {pin && <PropertyPin at={pin} />}
          {/* The area being drawn: its corners, then the shape as it closes. Nothing here catches a tap. */}
          {draft.length >= 3 && <Polygon positions={draft} pathOptions={{ color: BRAND, fillColor: BRAND, fillOpacity: 0.25, weight: 2, dashArray: '6 4', interactive: false }} />}
          {draft.length === 2 && <Polyline positions={draft} pathOptions={{ color: BRAND, weight: 2, dashArray: '6 4', interactive: false }} />}
          {draft.map((p, i) => <Marker key={`d${i}`} position={p} icon={draftDot} interactive={false} />)}
        </MapContainer>
        <TileChips tiles={tiles} onChange={setTiles} />
        {/* Below Leaflet's + / − (top-left, ~72px tall) and clear of the tile chips on the right. */}
        {hint && <div className="absolute top-20 left-2 z-10 max-w-[75%] rounded-lg bg-plum/85 px-2.5 py-1.5 text-xs font-semibold text-snow leading-snug">{hint}</div>}
      </div>

      <div className="border-t border-gray-200 bg-white px-2 sm:px-3 py-2 safe-area-inset-bottom">
        {mode === 'area' ? (
          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={() => setDraft(d => d.slice(0, -1))} disabled={!draft.length} className="btn-secondary min-h-[52px] flex items-center justify-center gap-1.5"><Undo2 className="h-4 w-4" /> Undo last point</button>
            <button type="button" onClick={() => { setDraft([]); setMode('idle'); }} className="btn-secondary min-h-[52px] flex items-center justify-center gap-1.5"><X className="h-4 w-4" /> Cancel</button>
            <button type="button" onClick={finishArea} disabled={draft.length < 3} className="btn-primary min-h-[52px] flex items-center justify-center gap-1.5"><Check className="h-4 w-4" /> Done</button>
          </div>
        ) : mode !== 'idle' ? (
          <div className="flex items-center gap-2">
            <p className="flex-1 text-sm font-semibold text-gray-800">Tap the map where the {mode} is.</p>
            <button type="button" onClick={() => setMode('idle')} className="btn-secondary min-h-[52px] px-5">Cancel</button>
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
            <Tool icon={Hexagon} label="Mark an area" disabled={readOnly} onClick={() => { setEditing(null); setMode('area'); }} />
            <Tool icon={Lock} label="Add a lock" disabled={readOnly} onClick={() => { setEditing(null); setMode('lock'); }} />
            <Tool icon={DoorOpen} label="Add a gate" disabled={readOnly} onClick={() => { setEditing(null); setMode('gate'); }} />
            <Tool icon={StickyNote} label="Add a note" disabled={readOnly} onClick={() => { setEditing(null); setMode('note'); }} />
            <Tool icon={MapPin} label="Move the pin" disabled={readOnly} onClick={() => { setEditing(null); setMode('pin'); }} />
          </div>
        )}
      </div>

      {editing != null && marks[editing] && (
        <MarkSheet mark={marks[editing]} picks={picks} readOnly={readOnly}
          onChange={m => update(ms => ms.map((x, j) => (j === editing ? m : x)))}
          onDelete={() => { update(ms => ms.filter((_, j) => j !== editing)); setEditing(null); }}
          onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function Tool({ icon: Icon, label, onClick, disabled }: { icon: React.ComponentType<{ className?: string }>; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="press flex flex-col items-center justify-center gap-1 rounded-xl border border-gray-300 bg-white text-gray-900 min-h-[56px] px-1 py-1.5 text-[11px] font-bold leading-tight disabled:opacity-40">
      <Icon className="h-5 w-5" />{label}
    </button>
  );
}

/** The bottom sheet for one mark: its name, its work or its code, notes, colour — or delete it. */
function MarkSheet({ mark, picks, readOnly, onChange, onDelete, onClose }: {
  mark: SiteMark; picks: string[]; readOnly: boolean;
  onChange: (m: SiteMark) => void; onDelete: () => void; onClose: () => void;
}) {
  // Escape closes it, and a tap on the dark backdrop does too — a sheet with
  // only a small ✕ is a trap on a phone held one-handed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const isArea = mark.kind === 'area';
  const hasCode = mark.kind === 'lock' || mark.kind === 'gate';
  const title = KIND_WORD[mark.kind];
  const set = (k: 'label' | 'work' | 'code' | 'notes') => (v: string) => onChange({ ...mark, [k]: v === '' ? null : v });
  const chip = (on: boolean) => `press min-h-[38px] text-sm font-semibold px-3 rounded-full border flex items-center gap-1.5 ${on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`;
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="bg-white text-gray-900 rounded-t-2xl w-full max-w-lg p-4 pb-6 space-y-3 max-h-[85dvh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black uppercase tracking-tight">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-gray-100"><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        <label className="block">
          <span className="text-xs font-semibold text-gray-600">Name</span>
          <input className="input mt-1" value={mark.label || ''} maxLength={60} disabled={readOnly}
            placeholder={isArea ? 'Front lawn, back lot, ditch…' : hasCode ? 'East gate, shop door…' : 'Dumpster, spigot, where to park…'}
            onChange={e => set('label')(e.target.value)} />
        </label>
        {isArea && (
          <div>
            <span className="text-xs font-semibold text-gray-600">What gets done here</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {picks.map(p => (
                <button key={p} type="button" disabled={readOnly} onClick={() => onChange({ ...mark, work: p })} className={chip(mark.work === p)}>
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: WORK_COLORS[p] || OTHER_COLOR }} />{p}
                </button>
              ))}
            </div>
            <input className="input mt-2" value={mark.work || ''} maxLength={120} disabled={readOnly} placeholder="Or say it your way" onChange={e => set('work')(e.target.value)} />
          </div>
        )}
        {hasCode && (
          <label className="block">
            <span className="text-xs font-semibold text-gray-600">Code</span>
            <input className="input mt-1 text-2xl font-black tabular-nums tracking-widest" value={mark.code || ''} maxLength={40} disabled={readOnly}
              autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder="e.g. 4471#" onChange={e => set('code')(e.target.value)} />
            <span className="block mt-1 text-[11px] text-gray-500">The crew see it on the job. It never goes on a customer's page.</span>
          </label>
        )}
        <label className="block">
          <span className="text-xs font-semibold text-gray-600">Notes</span>
          <textarea className="input mt-1" rows={2} maxLength={500} value={mark.notes || ''} disabled={readOnly}
            placeholder={hasCode ? 'Sticks — lift and turn. Lock it behind you.' : isArea ? 'Sprinkler heads along the curb.' : 'Anything the crew should know here.'}
            onChange={e => set('notes')(e.target.value)} />
        </label>
        <div>
          <span className="text-xs font-semibold text-gray-600">Colour on the map</span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <button type="button" disabled={readOnly} onClick={() => onChange({ ...mark, color: null })}
              className={`press min-h-[36px] px-3 rounded-full border text-xs font-bold ${mark.color == null ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}>Auto</button>
            {SWATCHES.map(c => (
              <button key={c} type="button" disabled={readOnly} aria-label={`Colour ${c}`} onClick={() => onChange({ ...mark, color: c })}
                className={`press h-9 w-9 rounded-full border-2 ${mark.color === c ? 'border-gray-900 ring-2 ring-offset-1 ring-brand-500' : 'border-white shadow-sm'}`} style={{ background: c }} />
            ))}
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" disabled={readOnly} onClick={() => { if (window.confirm('Take this mark off the map?')) onDelete(); }}
            className="press flex-1 min-h-[48px] rounded-xl border border-red-300 text-red-700 font-bold flex items-center justify-center gap-1.5 disabled:opacity-40">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
          <button type="button" onClick={onClose} className="btn-primary flex-1 min-h-[48px]">Done</button>
        </div>
      </div>
    </div>
  );
}
